import { FC, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FormikErrors } from "formik";
import { IContacts, ContactsFormValues } from "../../../domain/models/Contacts";
import BaseModal from "../../modals/BaseModal";
import CustomInput from "../../../custom-components/form-inputs/CustomInput";
import CustomError from "../../../custom-components/form-inputs/CustomError";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import { ContactsFields, contactsSchemaFactory } from "../../../validation-schemas/ContactsSchemas";
import { contactsServiceFactory } from "../../../services/ContactsServiceImpl";
import { managementServiceFactory } from "../../../services/ManagementServiceImpl";
import CustomDropdown from "../../../custom-components/form-inputs/CustomDropdown";
import { SelectProps } from "../../../types/SelectProps";
import { InputType } from "../../../types/InputType";

interface ContactsEditorProps {
    entity?: IContacts | null;
    onClose: () => void;
    onSave: (saved: IContacts) => void;
}

const ContactsEditor: FC<ContactsEditorProps> = ({ entity, onClose, onSave }) => {
    const { t } = useTranslation();
    const contactsService = contactsServiceFactory();
    const managementService = managementServiceFactory();
    const [managementOptions, setManagementOptions] = useState<SelectProps[]>([]);
    const [selectedDirector, setSelectedDirector] = useState<SelectProps | null>(null);

    useEffect(() => {
        const loadManagement = async () => {
            try {
                const result = await managementService.getAll(1, 1000, {});
                const options: SelectProps[] = [
                    { value: "", labelKey: "console.contacts.noDirector" },
                    ...result.rows
                        .filter((m) => m.translations && m.translations.length > 0)
                        .map((m) => ({
                            value: m.id,
                            labelKey: m.translations?.[0]?.fullName || m.id,
                        })),
                ];
                setManagementOptions(options);

                if (entity?.directorId) {
                    const director = result.rows.find((m) => m.id === entity.directorId);
                    if (director && director.translations && director.translations.length > 0) {
                        setSelectedDirector({
                            value: director.id,
                            labelKey: director.translations[0].fullName || director.id,
                        });
                    } else if (director) {
                        setSelectedDirector({
                            value: director.id,
                            labelKey: director.id,
                        });
                    } else {
                        setSelectedDirector(options[0]);
                    }
                } else {
                    setSelectedDirector(options[0]);
                }
            } catch (err) {
                console.log("Error loading management", err);
                setManagementOptions([{ value: "", labelKey: "console.contacts.noDirector" }]);
                setSelectedDirector({ value: "", labelKey: "console.contacts.noDirector" });
            }
        };
        loadManagement();
    }, [entity, managementService]);

    const { form } = useCustomFormik<ContactsFormValues>({
        initial: {
            address: entity?.address || "",
            phone: entity?.phone || "",
            email: entity?.email || "",
            directorId: entity?.directorId || "",
        },
        fields: ContactsFields,
        schema: contactsSchemaFactory(t),
        onSubmit: async (values) => {
            const updated: IContacts = {
                ...entity,
                ...values,
            };
            const saved = await contactsService.createOrUpdate(updated);
            if (saved) {
                onSave(saved);
            }
            onClose();
        },
    });

    useEffect(() => {
        if (selectedDirector && form.values.directorId !== selectedDirector.value) {
            form.setFieldValue("directorId", selectedDirector.value);
        }
    }, [selectedDirector]);

    return (
        <BaseModal
            isVisible={true}
            setIsVisible={onClose}
            title={entity ? t("console.contacts.editContacts") : t("console.contacts.createContacts")}
            confirmTitle={t("console.common.save")}
            cancelTitle={t("console.common.cancel")}
            confirmHandler={form.handleSubmit}
            closeHandler={onClose}
            additionalWrapClassName="w-[600px]"
            confirmButtonDisabled={!form.isValid}
        >
            <div className="flex flex-col gap-4">
                {Object.entries(ContactsFields).map(([key, cfg]) => {
                    const fieldName = key;
                    const value = form.values[key as keyof ContactsFormValues];
                    const errorKey = form.errors[key as keyof ContactsFormValues] as string;

                    if (key === "directorId") {
                        return (
                            <CustomDropdown
                                key={key}
                                labelKey={cfg.placeholderKey}
                                placeHolderKey="console.contacts.selectDirector"
                                dropDownCallback={(value) => {
                                    const option = managementOptions.find((o) => o.value === value);
                                    setSelectedDirector(option || null);
                                    form.setFieldValue("directorId", value);
                                }}
                                data={managementOptions}
                                defaultValue={selectedDirector || managementOptions[0]}
                                wrapperClassName="w-full"
                            />
                        );
                    }

                    return (
                        <CustomInput
                            key={key}
                            type={cfg.type as InputType}
                            labelKey={cfg.placeholderKey}
                            name={fieldName}
                            placeholderKey={cfg.placeholderKey}
                            value={value as string}
                            onChange={form.handleChange(fieldName)}
                            errorKey={errorKey}
                        />
                    );
                })}
            </div>
        </BaseModal>
    );
};

export default ContactsEditor;
